import { AgUiEventType } from '../types/gateway-chat-types';

/**
 * Color palette for event types in the Gateway Protocol Monitor.
 * These colors provide visual distinction between different event categories.
 */
export const EVENT_COLORS: Record<AgUiEventType, string> = {
  // Connection lifecycle events
  CONNECTION_ESTABLISHED: '#28a745', // Green - success
  CONNECTION_CLOSED: '#6c757d', // Gray - neutral

  // Session and history
  SESSION_HISTORY: '#17a2b8', // Cyan - informational

  // Run lifecycle
  RUN_STARTED: '#17a2b8', // Cyan - informational
  RUN_FINISHED: '#28a745', // Green - success

  // Text message streaming
  TEXT_MESSAGE_START: '#007bff', // Blue - primary
  TEXT_MESSAGE_CONTENT: '#fd7e14', // Orange - active
  TEXT_MESSAGE_END: '#6610f2', // Purple - complete

  // Tool execution
  TOOL_CALL_START: '#20c997', // Teal - tool activity
  TOOL_CALL_ARGS: '#20c997', // Teal - tool activity
  TOOL_CALL_END: '#20c997', // Teal - tool activity

  // Status and errors
  ERROR: '#dc3545', // Red - error
  PROGRESS_UPDATE: '#ffc107', // Yellow - in progress

  // Artifacts
  ARTIFACT_START: '#e83e8c', // Pink - artifact
  ARTIFACT_CHUNK: '#e83e8c', // Pink - artifact
  ARTIFACT_END: '#e83e8c', // Pink - artifact

  // Data visualization
  TABLE_DATA: '#6f42c1', // Purple - data
  CHART_DATA: '#6f42c1', // Purple - data

  // Metadata and citations
  CITATION_ADDED: '#17a2b8', // Cyan - reference
  WARNING: '#ffc107', // Yellow - warning
  STATUS_UPDATE: '#17a2b8', // Cyan - status
  METADATA: '#6c757d', // Gray - metadata

  // Interactive events
  INPUT_REQUEST: '#0ea5e9', // Sky - awaiting user input
  CONFIRMATION_REQUEST: '#0ea5e9', // Sky - awaiting user input
};

/**
 * Get the color for a specific event type.
 * Falls back to gray if event type is not recognized.
 */
export const getEventColor = (eventType: AgUiEventType): string => {
  return EVENT_COLORS[eventType] ?? '#6c757d';
};

/**
 * Confidence level color palette for message metadata.
 */
export const CONFIDENCE_COLORS = {
  HIGH: '#10b981', // Green - high confidence (> 0.8)
  MEDIUM: '#f59e0b', // Orange - medium confidence (0.6 - 0.8)
  LOW: '#ef4444', // Red - low confidence (< 0.6)
} as const;

/**
 * Get the color for a confidence value (0-1 scale).
 * Returns green for high (>0.8), orange for medium (0.6-0.8), and red for low (<0.6).
 */
export const getConfidenceColor = (value: number): string => {
  if (value > 0.8) return CONFIDENCE_COLORS.HIGH;
  if (value > 0.6) return CONFIDENCE_COLORS.MEDIUM;
  return CONFIDENCE_COLORS.LOW;
};
